import React from 'react';
import { Check, ArrowRight, BookOpen, Heart, Shield, Zap, Users } from 'lucide-react';
import { ProductCarousel } from './ProductCarousel';
interface SectionProps {
  onScrollToPrice: () => void;
}
export function IdentificationSection({
  onScrollToPrice
}: SectionProps) {
  return <section className="py-16 md:py-24 bg-gray-50 px-4" data-id="element-29">
      <div className="max-w-2xl mx-auto" data-id="element-30">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-31">
          Si esto te suena familiar, no estás solo/a...
        </h2>

        <div className="space-y-6 mb-10" data-id="element-32">
          {['Tu hijo se enoja cada vez que le quitás el celular', 'Negocia todo: 5 minutos más, un video más, un juego más', 'Sin pantalla dice que se aburre y no sabe qué hacer', 'Usás el celular para que se quede quieto y después sentís culpa', 'Querés poner límites pero terminás gritando o cediendo'].map((item, i) => <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-gray-100 shadow-sm" data-id="element-33">
              <div className="w-2 h-2 rounded-full bg-red-500 mt-2.5 shrink-0" data-id="element-34" />
              <p className="text-lg text-gray-700 leading-relaxed" data-id="element-35">{item}</p>
            </div>)}
        </div>

        <div className="bg-white p-6 md:p-8 rounded-2xl border-l-4 border-red-500 shadow-sm mb-10" data-id="element-36">
          <p className="text-lg md:text-xl font-medium text-gray-800 italic text-center" data-id="element-37">
            "No es tu culpa. Nadie nos preparó para criar en un mundo lleno de
            pantallas. Pero sí podés hacer algo hoy."
          </p>
        </div>

        <div className="text-center" data-id="element-38">
          <button onClick={onScrollToPrice} className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white text-lg font-bold py-4 px-8 rounded-xl shadow-md transition-all" data-id="element-39">
            Quiero dejar de pelear por el celular
          </button>
        </div>
      </div>
    </section>;
}
export function ShiftSection() {
  return <section className="py-16 md:py-24 bg-white px-4" data-id="element-40">
      <div className="max-w-2xl mx-auto text-center" data-id="element-41">
        <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6" data-id="element-42">
          El problema no es tu hijo. <br data-id="element-43" />
          <span className="text-red-600" data-id="element-44">Tampoco sos vos.</span>
        </h2>

        <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-45">
          El celular se convirtió en un{' '}
          <span className="font-bold text-gray-900" data-id="element-46">tercer adulto</span> en tu
          casa. Uno que no pide permiso, no pone límites y no enseña valores.
          Cada vez que una pantalla reemplaza tu presencia, tu hijo aprende que
          la calma viene de afuera, no de adentro.
        </p>

        <div className="inline-block bg-yellow-50 border border-yellow-200 rounded-lg px-6 py-4" data-id="element-47">
          <p className="text-yellow-800 font-semibold" data-id="element-48">
            💡 El celular no es el enemigo. El uso sin límites, sí.
          </p>
        </div>
      </div>
    </section>;
}
export function ProductPresentationSection({
  onScrollToPrice
}: SectionProps) {
  return <section className="py-16 md:py-24 bg-gray-50 px-4" data-id="element-49">
      <div className="max-w-3xl mx-auto text-center" data-id="element-50">
        <span className="text-red-600 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-51">
          Presentamos
        </span>
        <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4" data-id="element-52">
          Crianza Sin Celular
        </h2>
        <p className="text-xl md:text-2xl text-gray-600 font-medium mb-10" data-id="element-53">
          El programa que te devuelve la calma y la autoridad amorosa en casa.
        </p>

        <div className="mb-12" data-id="element-54">
          <ProductCarousel onScrollToPrice={onScrollToPrice} data-id="element-55" />
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 mb-10 text-left" data-id="element-56">
          <p className="text-lg text-gray-800 mb-6" data-id="element-57">
            <span className="font-bold" data-id="element-58">Es:</span> Un programa digital en 5
            tomos con estrategias claras, simples y aplicables para reducir
            pantallas, poner límites sanos y reconectar con tus hijos.
          </p>

          <div className="space-y-3" data-id="element-59">
            <p className="font-bold text-gray-900 mb-2" data-id="element-60">Lo que NO es:</p>
            {['No es prohibir el celular de golpe', 'No es gritar ni castigar', 'No es teoría ni lenguaje técnico', 'No es para padres perfectos'].map((item, i) => <div key={i} className="flex items-center gap-2 text-gray-600" data-id="element-61">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-400" data-id="element-62" />
                <span data-id="element-63">{item}</span>
              </div>)}
          </div>
        </div>

        <div className="bg-green-50 rounded-xl p-6 mb-10 border border-green-100" data-id="element-64">
          <p className="text-green-800 font-medium text-lg" data-id="element-65">
            Es para padres reales que quieren recuperar su lugar como guía, sin
            culpa y sin guerra.
          </p>
        </div>

        <div className="relative mx-auto max-w-[350px] mb-10 cursor-pointer hover:opacity-95 transition-opacity" onClick={onScrollToPrice} data-id="element-66">
          <img src="https://cdn.magicpatterns.com/uploads/1GPopg84LWSHD2F5Ug43t5/ChatGPT_Image_5_feb_2026,_06_31_36_p.m..png" alt="Crianza Sin Celular - Saga completa de 5 tomos" className="w-full h-auto drop-shadow-xl rounded-xl" data-id="element-67" />
        </div>

        <button onClick={onScrollToPrice} className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white text-lg font-bold py-4 px-8 rounded-xl shadow-md transition-all" data-id="element-68">
          Quiero acceder al programa
        </button>
      </div>
    </section>;
}
export function ModulesSection() {
  const modules = [{
    icon: <BookOpen className="w-6 h-6 text-red-500" data-id="element-69" />,
    title: 'Tomo 1',
    desc: 'Entendé por qué el celular tiene tanto poder sobre tu hijo'
  }, {
    icon: <Zap className="w-6 h-6 text-yellow-500" data-id="element-70" />,
    title: 'Tomo 2',
    desc: 'Descubrí el impacto real en sus emociones y conducta'
  }, {
    icon: <Shield className="w-6 h-6 text-blue-500" data-id="element-71" />,
    title: 'Tomo 3',
    desc: 'Aprendé a poner límites sin gritos ni culpa'
  }, {
    icon: <Users className="w-6 h-6 text-green-500" data-id="element-72" />,
    title: 'Tomo 4',
    desc: 'Encontrá actividades que reemplacen la pantalla de verdad'
  }, {
    icon: <Heart className="w-6 h-6 text-purple-500" data-id="element-73" />,
    title: 'Tomo 5',
    desc: 'Construí hábitos familiares que sostengan el cambio'
  }];
  return <section className="py-16 md:py-24 bg-white px-4" data-id="element-74">
      <div className="max-w-3xl mx-auto" data-id="element-75">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-76">
          ¿Qué vas a lograr con cada tomo?
        </h2>

        <div className="space-y-6" data-id="element-77">
          {modules.map((mod, i) => <div key={i} className="flex items-start gap-4 p-6 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-78">
              <div className="bg-gray-50 p-3 rounded-lg shrink-0" data-id="element-79">
                {mod.icon}
              </div>
              <div data-id="element-80">
                <span className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-1 block" data-id="element-81">
                  {mod.title}
                </span>
                <p className="text-lg font-medium text-gray-900" data-id="element-82">{mod.desc}</p>
              </div>
            </div>)}
        </div>

        <div className="mt-10 text-center" data-id="element-83">
          <p className="text-gray-500 text-sm font-medium bg-gray-50 inline-block px-4 py-2 rounded-full" data-id="element-84">
            📱 Todo digital. Acceso inmediato. Desde cualquier dispositivo.
          </p>
        </div>
      </div>
    </section>;
}
export function GuaranteeSection() {
  return <section className="py-16 bg-white px-4 border-t border-gray-100" data-id="element-85">
      <div className="max-w-2xl mx-auto" data-id="element-86">
        <div className="bg-gray-50 rounded-2xl p-8 md:p-10 text-center border border-gray-100" data-id="element-87">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-88">
            <Shield className="w-8 h-8 text-green-600" data-id="element-89" />
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-90">
            Garantía real, sin vueltas
          </h3>
          <div className="space-y-4 text-gray-600 text-lg" data-id="element-91">
            <p data-id="element-92">Probá el programa.</p>
            <p data-id="element-93">
              Si no sentís que te sirve,{' '}
              <span className="font-bold text-gray-900" data-id="element-94">
                te devolvemos la plata.
              </span>
            </p>
            <p data-id="element-95">Sin preguntas.</p>
            <p data-id="element-96">Sin complicaciones.</p>
          </div>
        </div>
      </div>
    </section>;
}
export function FinalCTA({
  checkoutUrl
}: {
  checkoutUrl: string;
}) {
  return <section className="py-20 md:py-24 bg-white px-4 text-center" data-id="element-97">
      <div className="max-w-2xl mx-auto" data-id="element-98">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-99">
          Hoy puede ser el día en que todo empiece a cambiar.
        </h2>
        <p className="text-xl text-gray-600 mb-10" data-id="element-100">
          No necesitás ser perfecto/a. Solo necesitás dar el primer paso.
        </p>

        <a href={checkoutUrl} className="inline-block w-full md:w-auto bg-red-600 hover:bg-red-700 text-white text-xl font-bold py-5 px-10 rounded-xl shadow-lg hover:shadow-xl transition-all" data-id="element-101">
          Quiero empezar hoy
        </a>
      </div>
    </section>;
}
export function CreatorSection() {
  return <section className="py-16 md:py-24 bg-white px-4" data-id="element-102">
      <div className="max-w-2xl mx-auto text-center" data-id="element-103">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8" data-id="element-104">
          ¿Quién creó este programa?
        </h2>

        <div className="bg-gray-50 rounded-2xl p-8 md:p-10 border border-gray-100" data-id="element-105">
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed" data-id="element-106">
            <p className="font-semibold text-gray-900 text-xl" data-id="element-107">
              Soy madre/padre como vos.
            </p>
            <p data-id="element-108">
              Viví el cansancio, la culpa y las peleas diarias por el celular.
            </p>
            <p data-id="element-109">
              Este programa fue creado para{' '}
              <span className="font-bold text-gray-900" data-id="element-110">
                familias reales como la tuya:
              </span>
            </p>
            <p data-id="element-111">práctico, claro y aplicable desde el día 1.</p>
          </div>
        </div>
      </div>
    </section>;
}
export function InvestmentValueSection({
  onScrollToPrice
}: SectionProps) {
  return <section className="py-16 md:py-24 bg-gray-50 px-4" data-id="element-112">
      <div className="max-w-2xl mx-auto text-center" data-id="element-113">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8" data-id="element-114">
          ¿Qué estás invirtiendo realmente?
        </h2>

        <div className="space-y-6 text-lg text-gray-700 leading-relaxed" data-id="element-115">
          <p className="font-semibold text-gray-900 text-xl" data-id="element-116">
            No es un ebook más.
          </p>
          <p data-id="element-117">
            Es un{' '}
            <span className="font-bold text-gray-900" data-id="element-118">
              programa completo con 5 tomos + bonos prácticos.
            </span>
          </p>
          <div className="bg-white rounded-xl p-6 border border-gray-100 shadow-sm" data-id="element-119">
            <p className="text-2xl font-bold text-gray-900 mb-2" data-id="element-120">
              Por menos de $4.000 por cada tomo,
            </p>
            <p className="text-gray-600" data-id="element-121">
              accedés a herramientas que podés aplicar desde el primer día.
            </p>
          </div>
          <p data-id="element-122">
            Es una inversión pequeña comparada con el{' '}
            <span className="font-bold text-red-600" data-id="element-123">desgaste emocional</span>{' '}
            que hoy te está costando el celular en casa.
          </p>
        </div>

        <div className="mt-10" data-id="element-124">
          <button onClick={onScrollToPrice} className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white text-lg font-bold py-4 px-8 rounded-xl shadow-md transition-all" data-id="element-125">
            Ver la oferta especial
          </button>
        </div>
      </div>
    </section>;
}